import { selectionIsLocked } from '@/types/selectionWorkflow'
import { InvitationStatus } from './InvitationStatus'
import { useEffect, useState } from 'react'
import { Trash2, ExternalLink, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { useCuratorComments } from '../hooks/useCuratorComments'
import type { Submission, CurationColumnId } from '../hooks/useCurationSubmissions'
import { CURATION_COLUMNS, statusToColumn } from '../hooks/useCurationSubmissions'
import { adminFormBuilderService } from '@/features/admin/services/admin.formBuilder.service'
import type { ApplicationFormDocument } from '@/types'

interface Props {
  submission: Submission | null
  onClose: () => void
  onNotesChange: (submissionId: string, notes: string) => void
  onMove: (submissionId: string, toColumn: CurationColumnId, toIndex: number) => void
  /** When non-null, renders a Prev arrow + binds ← to it. */
  onPrev?: (() => void) | null
  /** When non-null, renders a Next arrow + binds → to it. */
  onNext?: (() => void) | null
  /** Optional position indicator shown in the toolbar (e.g. "3 of 12"). */
  position?: { index: number; total: number } | null
}

/**
 * Full-detail view of a single submission. Renders the applicant info, every
 * answer with its label, file uploads, payment metadata, the existing inline
 * curator notes (editable here in a textarea), and the curator comments thread.
 */
export function SubmissionDetailModal({
  submission,
  onClose,
  onNotesChange,
  onMove,
  onPrev,
  onNext,
  position,
}: Props) {
  const open = submission !== null
  const submissionId = submission?.id ?? null
  const {
    comments,
    loading: commentsLoading,
    add,
    remove,
    currentUserId,
  } = useCuratorComments(submissionId)

  const [notesDraft, setNotesDraft] = useState(submission?.curatorNotes ?? '')
  const [newComment, setNewComment] = useState('')
  const [posting, setPosting] = useState(false)
  const [form, setForm] = useState<ApplicationFormDocument | null>(null)

  // Load the form schema so we can render answers in their original
  // section/field order rather than the arbitrary Firestore map order.
  const formId = submission?.formId ?? null
  useEffect(() => {
    if (!formId) {
      setForm(null)
      return
    }
    let cancelled = false
    adminFormBuilderService
      .getById(formId)
      .then((f) => {
        if (!cancelled) setForm(f)
      })
      .catch(() => {
        if (!cancelled) setForm(null)
      })
    return () => {
      cancelled = true
    }
  }, [formId])

  // Reset local form state when switching between submissions
  useEffect(() => {
    setNotesDraft(submission?.curatorNotes ?? '')
    setNewComment('')
  }, [submission?.id, submission?.curatorNotes])

  // Arrow-key navigation. Ignored when the user is typing in an editable field
  // so notes/comments composition isn't hijacked.
  useEffect(() => {
    if (!open) return
    function handler(e: KeyboardEvent) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return
      }
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault()
        onNext()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onPrev, onNext])

  if (!submission) {
    return (
      <Dialog open={false} onOpenChange={onClose}>
        <DialogContent />
      </Dialog>
    )
  }

  const fullName =
    `${submission.applicantInfo.firstName} ${submission.applicantInfo.lastName}`.trim() ||
    'Unknown applicant'
  const currentColumn = statusToColumn(submission.status)

  function commitNotes() {
    if (!submission) return
    if (notesDraft === (submission.curatorNotes ?? '')) return
    onNotesChange(submission.id, notesDraft)
  }

  async function postComment() {
    if (!newComment.trim()) return
    setPosting(true)
    try {
      await add(newComment)
      setNewComment('')
    } finally {
      setPosting(false)
    }
  }

  const answers = submission.answers ?? {}
  const fileUrls = submission.fileUrls ?? {}
  const orderedSections = buildOrderedSections(form, answers)
  // Field ids that were rendered via the form schema — anything left over
  // (e.g. legacy answers whose field was removed from the form) gets dumped
  // into an "Other" bucket so it isn't silently lost.
  const renderedIds = new Set(orderedSections.flatMap((s) => s.fields.map((f) => f.id)))
  const orphanAnswers = Object.entries(answers).filter(([id]) => !renderedIds.has(id))
  // Files for fields rendered inline are shown beside their answer; this
  // remaining-files section only surfaces orphans (file fields no longer in the form).
  const fileEntries = Object.entries(fileUrls).filter(
    ([id, urls]) => urls.length > 0 && !renderedIds.has(id)
  )

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[95vw] !max-w-[min(95vw,1100px)] overflow-y-auto sm:w-[90vw] sm:!max-w-[min(95vw,1100px)]">
        {(onPrev || onNext || position) && (
          <div className="flex items-center justify-between gap-2 border-b pb-2 text-xs text-muted-foreground">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onPrev?.()}
              disabled={!onPrev}
              aria-label="Previous submission (←)"
              title="Previous submission (←)"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {position && (
              <span className="font-mono">
                {position.index} of {position.total}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onNext?.()}
              disabled={!onNext}
              aria-label="Next submission (→)"
              title="Next submission (→)"
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}

        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{fullName}</DialogTitle>
              <DialogDescription>{submission.applicantInfo.email}</DialogDescription>
            </div>
            <Badge variant="default" className="shrink-0">
              {CURATION_COLUMNS.find((c) => c.id === currentColumn)?.title ?? submission.status}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Meta strip */}
          <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 px-4 py-3 text-sm sm:grid-cols-4">
            <Meta label="Submitted" value={formatTs(submission.submittedAt)} />
            <Meta label="Status" value={submission.status} />
            <Meta
              label="Rank"
              value={submission.rank !== undefined ? String(submission.rank) : '—'}
            />
            <Meta
              label="Invitation"
              value={selectionIsLocked(submission) ? 'Started' : 'Not started'}
            />
          </div>

          <InvitationStatus submission={submission} />
          {selectionIsLocked(submission) && (
            <p className="text-sm text-muted-foreground">
              Selection is locked because an invitation or participant response is recorded. Manage
              participation from Participants.
            </p>
          )}
          {/* Quick move buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Move to:</span>
            {CURATION_COLUMNS.map((c) => (
              <Button
                key={c.id}
                size="sm"
                variant={c.id === currentColumn ? 'default' : 'outline'}
                onClick={() => onMove(submission.id, c.id, 0)}
                disabled={c.id === currentColumn || selectionIsLocked(submission)}
              >
                {c.title}
              </Button>
            ))}
          </div>

          <Separator />

          {/* Answers — rendered in original form order, grouped by section */}
          <section className="space-y-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Application Answers
            </h3>
            {orderedSections.length === 0 && orphanAnswers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No answers recorded.</p>
            ) : (
              <>
                {orderedSections.map((section) => (
                  <div key={section.id} className="space-y-3">
                    <div className="space-y-0.5">
                      <h4 className="text-sm font-semibold">{section.title}</h4>
                      {section.description && (
                        <p className="text-xs text-muted-foreground">{section.description}</p>
                      )}
                    </div>
                    <dl className="space-y-3 border-l-2 border-muted pl-4">
                      {section.fields.map((field) => {
                        const answer = answers[field.id]
                        const urls = fileUrls[field.id] ?? []
                        return (
                          <div key={field.id} className="space-y-0.5">
                            <dt className="text-xs font-medium text-muted-foreground">
                              {answer?.label ?? field.label}
                            </dt>
                            <dd className="whitespace-pre-wrap text-sm">
                              {urls.length > 0 ? (
                                <div className="flex flex-wrap gap-2 pt-1">
                                  {urls.map((url) => (
                                    <FilePreview key={url} url={url} />
                                  ))}
                                </div>
                              ) : answer ? (
                                renderValue(answer.value)
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </dd>
                          </div>
                        )
                      })}
                    </dl>
                  </div>
                ))}
                {orphanAnswers.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Other</h4>
                    <dl className="space-y-3 border-l-2 border-muted pl-4">
                      {orphanAnswers.map(([fieldId, answer]) => (
                        <div key={fieldId} className="space-y-0.5">
                          <dt className="text-xs font-medium text-muted-foreground">
                            {answer.label}
                          </dt>
                          <dd className="whitespace-pre-wrap text-sm">
                            {renderValue(answer.value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Files */}
          {fileEntries.length > 0 && (
            <>
              <Separator />
              <section className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  File Uploads
                </h3>
                <div className="space-y-4">
                  {fileEntries.map(([fieldId, urls]) => {
                    const label = submission.answers?.[fieldId]?.label ?? fieldId
                    return (
                      <div key={fieldId} className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">{label}</p>
                        <div className="flex flex-wrap gap-2">
                          {urls.map((url) => (
                            <FilePreview key={url} url={url} />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            </>
          )}

          {/* Payment status (only if relevant) */}
          {(submission.paymentStatus || submission.paymentAmountCents) && (
            <>
              <Separator />
              <section className="space-y-2 text-sm">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Payment
                </h3>
                {submission.paymentStatus && (
                  <Meta label="Status" value={submission.paymentStatus} />
                )}
                {submission.paymentAmountCents !== undefined && (
                  <Meta
                    label="Amount"
                    value={formatMoney(
                      submission.paymentAmountCents,
                      submission.paymentCurrency ?? 'usd'
                    )}
                  />
                )}
              </section>
            </>
          )}

          <Separator />

          {/* Curator notes (single inline note — quick scratch pad) */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Curator Notes
            </h3>
            <Textarea
              rows={3}
              placeholder="Quick notes about this submission (visible only to admins)…"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={commitNotes}
            />
            <p className="text-xs text-muted-foreground">
              Saves on blur. A single editable note that also surfaces on the card.
            </p>
          </section>

          <Separator />

          {/* Comments thread */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Comments ({comments.length})
            </h3>

            <div className="space-y-2">
              {commentsLoading ? (
                <p className="text-sm text-muted-foreground">Loading comments…</p>
              ) : comments.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">
                  No comments yet. Start the thread below.
                </p>
              ) : (
                comments.map((c) => (
                  <div key={c.id} className="rounded-md border bg-muted/20 p-3">
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{c.authorName}</span>
                      <div className="flex items-center gap-2">
                        <span>{formatTs(c.createdAt as never)}</span>
                        {c.authorUid === currentUserId && (
                          <button
                            type="button"
                            onClick={() => remove(c.id)}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Delete comment"
                            title="Delete comment"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{c.body}</p>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-2">
              <Textarea
                rows={2}
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  // Cmd/Ctrl+Enter posts quickly
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault()
                    void postComment()
                  }
                }}
              />
              <div className="flex items-center justify-end gap-2">
                <span className="text-xs text-muted-foreground">⌘↵ to post</span>
                <Button size="sm" onClick={postComment} disabled={posting || !newComment.trim()}>
                  {posting && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Post Comment
                </Button>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Renders a single uploaded file. Detects images by filename extension and
 * shows an inline thumbnail; falls back to a text link for everything else.
 * Clicking either form opens the original asset in a new tab.
 */
function FilePreview({ url }: { url: string }) {
  const filename = filenameFromUrl(url)
  const isImage = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(filename)

  if (isImage) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={filename}
        className="group block overflow-hidden rounded-md border bg-muted/30 transition-opacity hover:opacity-90"
      >
        <img
          src={url}
          alt={filename}
          className="h-28 w-28 object-cover"
          onError={(e) => {
            // If the image fails to load (e.g. broken URL, expired token), fall
            // back to a tile that still lets the curator click through.
            const target = e.currentTarget
            target.style.display = 'none'
            const fallback = target.nextElementSibling as HTMLElement | null
            if (fallback) fallback.style.display = 'flex'
          }}
        />
        <div
          className="hidden h-28 w-28 items-center justify-center text-xs text-muted-foreground"
          style={{ display: 'none' }}
        >
          <span className="px-2 text-center">{filename}</span>
        </div>
      </a>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm text-primary hover:bg-muted/40 hover:underline"
    >
      {filename}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

interface OrderedSection {
  id: string
  title: string
  description?: string
  fields: { id: string; label: string }[]
}

function buildOrderedSections(
  form: ApplicationFormDocument | null,
  answers: Record<string, { label: string; value: unknown }>
): OrderedSection[] {
  if (!form) return []
  const sections = [...(form.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return sections.map((s) => ({
    id: s.id,
    title: s.title,
    description: s.description,
    fields: [...(s.fields ?? [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((f) => ({ id: f.id, label: answers[f.id]?.label ?? f.label })),
  }))
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function filenameFromUrl(url: string): string {
  try {
    const decoded = decodeURIComponent(url.split('?')[0] ?? url)
    const last = decoded.split('/').pop() ?? url
    return last || url
  } catch {
    return url
  }
}

function formatTs(ts: { toMillis?: () => number } | undefined): string {
  const ms = ts?.toMillis?.()
  if (!ms) return '—'
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}
